import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Printer, Download, ArrowLeft, Bookmark, BookmarkCheck, Check, CircleDashed, Volume2, VolumeX, Brain, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useBookmarks, useProgress } from '../hooks/useLocal';
import { brand } from '../mock';
import { toast } from '../hooks/use-toast';

export default function NoteViewerPage({ note, category, onBack }) {
  const navigate = useNavigate();
  const { isBookmarked, toggle } = useBookmarks();
  const { isRead, markRead, unmark } = useProgress();
  const bookmarked = isBookmarked(note.id);
  const read = isRead(note.id);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    return () => { if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel(); };
  }, []);

  const handlePrint = () => {
    toast({ title: 'A4 PDF ଡାଉନଲୋଡ', description: 'ପ୍ରିଣ୍ଟ ଡାୟଲଗ ଖୋଲୁଛି · Save as PDF ବାଛନ୍ତୁ' });
    setTimeout(() => window.print(), 250);
  };

  const toggleSpeech = () => {
    if (typeof speechSynthesis === 'undefined') { toast({ title: 'Browser TTS support ନାହିଁ' }); return; }
    if (speaking) { speechSynthesis.cancel(); setSpeaking(false); return; }
    const text = `${note.chapter}. ${note.sections.map((s) => `${s.heading}. ${s.body}`).join('. ')}`;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'or-IN';
    utter.rate = 0.85;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    speechSynthesis.speak(utter);
    setSpeaking(true);
    toast({ title: '🔊 ପାଠ ଶୁଣୁଛନ୍ତି', description: 'ପୁଣି ଚିପନ୍ତୁ ବନ୍ଦ କରିବା ପାଇଁ' });
  };

  const handleBack = () => { if (onBack) onBack(); else navigate(-1); };

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Non-print controls */}
      <div className="no-print space-y-3">
        <button onClick={handleBack} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
          <ArrowLeft size={16} /> ପଛ କୁ ଫେରନ୍ତୁ
        </button>

        <div className="flex gap-2">
          <Button onClick={handlePrint}
            className="flex-1 h-11 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2">
            <Download size={17} /> A4 PDF ଡାଉନଲୋଡ
          </Button>
          <Button onClick={handlePrint} variant="outline" className="h-11 rounded-2xl gap-2">
            <Printer size={16} /> Print
          </Button>
          <Button onClick={toggleSpeech} variant="outline" className={`h-11 rounded-2xl gap-1 ${speaking ? 'bg-red-50 border-red-300 text-red-700' : ''}`}>
            {speaking ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </Button>
        </div>

        <div className="flex gap-2">
          <button onClick={() => { toggle({ id: note.id, title: note.chapter, subject: note.subject, category }); toast({ title: bookmarked ? 'ବୁକମାର୍କ ହଟିଗଲା' : 'ବୁକମାର୍କ ହେଲା' }); }}
            className={`flex-1 h-10 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${bookmarked ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-700'}`}>
            {bookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
            {bookmarked ? 'ଚିହ୍ନିତ' : 'ଚିହ୍ନିତ କରନ୍ତୁ'}
          </button>
          <button onClick={() => { read ? unmark(note.id) : markRead(note.id); toast({ title: read ? 'ଅପଠିତ ଚିହ୍ନିତ' : 'ପଢ଼ା ହୋଇଲା' }); }}
            className={`flex-1 h-10 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${read ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-700'}`}>
            {read ? <Check size={16} /> : <CircleDashed size={16} />}
            {read ? 'ପଢ଼ିଛି' : 'ପଢ଼ିଲା ବୋଲି ଚିହ୍ନ'}
          </button>
        </div>
      </div>

      {/* Printable content wrapper */}
      <article className="print-page bg-white mt-5 p-6 rounded-2xl border border-gray-200 shadow-sm">
        {/* Header */}
        <header className="print-header text-center border-b-2 border-emerald-600 pb-3 mb-4">
          <h1 className="text-2xl font-extrabold text-emerald-700">{brand.name}</h1>
          <p className="text-xs text-gray-600">{brand.nameOdia} · {brand.taglineEn}</p>
        </header>

        <div className="mb-4">
          <div className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">{note.level}</div>
          <h2 className="text-xl font-extrabold text-gray-900 mt-1">{note.chapter}</h2>
          <div className="text-sm text-gray-600">{note.chapterEn}</div>
          <div className="text-xs text-gray-500 mt-1">ବିଷୟ / Subject: <span className="font-semibold text-gray-800">{note.subject}</span></div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {note.sections.map((s, i) => (
            <section key={i} className="note-section">
              <h3 className="text-base font-bold text-gray-900 bg-emerald-50 px-3 py-1.5 rounded-md border-l-4 border-emerald-500">
                {i + 1}. {s.heading}
              </h3>
              <p className="text-sm text-gray-800 mt-2 leading-relaxed whitespace-pre-line" style={{ textAlign: 'justify' }}>
                {s.body}
              </p>
            </section>
          ))}
        </div>

        {/* Footer */}
        <footer className="print-footer border-t border-gray-200 mt-6 pt-3 text-[10px] text-gray-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} {brand.name} · {brand.email}</div>
          <div>Page 1 of {note.pages}</div>
        </footer>
      </article>

      {/* Related quiz suggestion */}
      <Card className="no-print mt-4 rounded-2xl p-4 border-purple-200 bg-gradient-to-br from-fuchsia-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white flex items-center justify-center">
            <Brain size={19} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm text-purple-900">ଏହି ପାଠ ପାଇଁ Quiz ଦିଅନ୍ତୁ</div>
            <div className="text-[11px] text-purple-700">Timer + scoring ସହିତ ନିଜକୁ ଯାଞ୍ଚ କରନ୍ତୁ</div>
          </div>
          <Link to="/quiz" className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-3 py-2 rounded-full flex items-center gap-1">
            ଆରମ୍ଭ <ChevronRight size={12} />
          </Link>
        </div>
      </Card>
    </div>
  );
}
